import React, { useState, useEffect } from 'react';
import { WordList } from '../components/vocabulary/WordList';
import { AddWordDialog } from '../components/vocabulary/AddWordDialog';
import { EditWordDialog } from '../components/vocabulary/EditWordDialog';
import { WordDetailsModal } from '../components/vocabulary/WordDetailsModal';
import { InterestsModal } from '../components/onboarding/InterestsModal';
import { OnboardingTour, isTourCompleted } from '../components/onboarding/OnboardingTour';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { ErrorNotification } from '../components/common/ErrorNotification';
import { Button } from '../components/common/Button';
import { useWords } from '../hooks/useWords';
import apiClient from '../services/api';

/**
 * Home page - displays user's vocabulary list
 */
const HomePage = () => {
  const {
    groupedWords,
    groupBy,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
    createWord,
    updateWord,
    deleteWord,
    updateLearningLevel,
    changeGrouping,
    reset,
    // Search functionality
    searchQuery,
    isSearching,
    searchWords,
    clearSearch,
  } = useWords();

  const [runTour, setRunTour] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInterestsModal, setShowInterestsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [viewingWord, setViewingWord] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger',
  });
  const [errorNotification, setErrorNotification] = useState(null);

  // Check if user has completed profile (interests + native language) on mount
  useEffect(() => {
    const checkInterests = async () => {
      try {
        const response = await apiClient.getInterests();
        if (!response.exists || !response.nativeLanguage) {
          setShowInterestsModal(true);
        }
      } catch (error) {
        console.error('Error checking interests:', error);
      }
    };

    checkInterests();
  }, []);

  // Listen for auth events to reset state
  useEffect(() => {
    const handleLogout = () => {
      reset();
    };

    const handleUserChanged = () => {
      reset();
      setTimeout(() => refresh(), 0);
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:userChanged', handleUserChanged);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:userChanged', handleUserChanged);
    };
  }, [reset, refresh]);

  const handleAddWord = async (word) => {
    try {
      const response = await createWord(word, true);
      return response;
    } catch (error) {
      console.error('Error adding word:', error);
      setErrorNotification(
        error.response?.data?.error ||
        error.message ||
        'Failed to add word. Please check your connection and try again.'
      );
      throw error;
    }
  };

  const handleEditWord = (word) => {
    setEditingWord(word);
    setShowEditDialog(true);
  };

  const handleUpdateWord = async (wordId, data) => {
    try {
      const updated = await updateWord(wordId, data);
      return updated;
    } catch (error) {
      console.error('Error updating word:', error);
      setErrorNotification(
        error.response?.data?.error ||
        error.message ||
        'Failed to update word. Please check your connection and try again.'
      );
      throw error;
    }
  };

  const handleDeleteWord = (wordId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Word',
      message: 'Are you sure you want to delete this word? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteWord(wordId);
        } catch (error) {
          console.error('Error deleting word:', error);
          setErrorNotification(
            error.response?.data?.error ||
            error.message ||
            'Failed to delete word. Please check your connection and try again.'
          );
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      variant: 'danger',
    });
  };

  const handleWordClick = (word) => {
    setViewingWord(word);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setViewingWord(null);
  };

  return (
    <>
      {/* Page Header with Add Button */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">My Vocabulary</h2>
          <span data-tour="add-word-btn">
            <Button onClick={() => setShowAddDialog(true)} size="md">
              + New Word
            </Button>
          </span>
        </div>
      </div>

      {/* Word List */}
      <div className="max-w-7xl mx-auto" data-tour="word-list">
        <WordList
          groupedWords={groupedWords}
          groupBy={groupBy}
          onChangeGrouping={changeGrouping}
          isLoading={isLoading}
          error={error}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onEdit={handleEditWord}
          onDelete={handleDeleteWord}
          onUpdateLearningLevel={updateLearningLevel}
          onRetry={refresh}
          onWordClick={handleWordClick}
          searchQuery={searchQuery}
          isSearching={isSearching}
          onSearch={searchWords}
          onClearSearch={clearSearch}
        />
      </div>

      {/* Dialogs */}
      <AddWordDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddWord}
      />

      <EditWordDialog
        isOpen={showEditDialog}
        word={editingWord}
        onClose={() => {
          setShowEditDialog(false);
          setEditingWord(null);
        }}
        onUpdate={handleUpdateWord}
      />

      {viewingWord && (
        <WordDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          word={viewingWord}
        />
      )}

      <InterestsModal
        isOpen={showInterestsModal}
        onClose={() => setShowInterestsModal(false)}
        onSuccess={() => {
          setShowInterestsModal(false);
          refresh();
          setRunTour(true);
        }}
      />

      <OnboardingTour run={runTour} onFinish={() => setRunTour(false)} />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />

      <ErrorNotification
        message={errorNotification}
        onClose={() => setErrorNotification(null)}
      />
    </>
  );
};

export default HomePage;
